import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Clock, MapPin, Calendar, Users, Truck, Star, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerQuotesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestId = searchParams.get('request_id') || '';
  // Prefer email from URL (when navigated from dashboard), otherwise use signed-in customer's email
  const customerFromStorage = (() => {
    try {
      const stored = sessionStorage.getItem('customer');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const email = searchParams.get('email') || customerFromStorage?.email || '';

  const [tripRequest, setTripRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState(null);
  const [acceptedQuoteId, setAcceptedQuoteId] = useState(null);

  const runSearch = useCallback(async (reqId, customerEmail) => {
    if (!reqId || !customerEmail) {
      setError('Missing request ID or email. Please open a trip from your dashboard.');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      const tripResponse = await axios.get(`${API}/trip-requests/${reqId}`);
      const tripData = tripResponse.data;

      if (tripData.email.toLowerCase() !== customerEmail.toLowerCase()) {
        setError('This trip request does not belong to your account.');
        setTripRequest(null);
        setQuotes([]);
        setLoading(false);
        return;
      }

      setTripRequest(tripData);
      const quotesResponse = await axios.get(`${API}/quotes/trip/${reqId}`);
      setQuotes(quotesResponse.data);
      if (tripData.accepted_quote_id) {
        setAcceptedQuoteId(tripData.accepted_quote_id);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Request not found. Please check your Request ID.');
      } else {
        setError('Failed to load quotes. Please try again.');
      }
      setTripRequest(null);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-search on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      await runSearch(requestId, email);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId, email, runSearch]);

  const handleAcceptQuote = async (quoteId) => {
    setAcceptingQuoteId(quoteId);
    setError('');

    try {
      await axios.post(`${API}/quotes/${quoteId}/accept`, {
        customer_email: email
      });
      
      setAcceptedQuoteId(quoteId);
      
      // Update trip request status locally
      setTripRequest(prev => ({
        ...prev,
        status: 'accepted',
        accepted_quote_id: quoteId
      }));
      
      // Update quote status locally
      setQuotes(prev => prev.map(q => ({
        ...q,
        status: q.quote_id === quoteId ? 'accepted' : (q.status === 'pending' ? 'rejected' : q.status)
      })));
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept quote. Please try again.');
    } finally {
      setAcceptingQuoteId(null);
    }
  };

  const getServiceBadgeColor = (serviceType) => {
    switch (serviceType?.toLowerCase()) {
      case 'premium': return 'bg-amber-500 text-black';
      case 'express': return 'bg-red-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium">Paid</span>;
      case 'accepted':
        return <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">Accepted</span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">Not Selected</span>;
      default:
        return <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">Pending</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Your <span className="text-amber-500">Quotes</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Compare quotes from verified transporters and book the right one for your trip.
            </p>
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <section className="pb-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-500" data-testid="error-message">{error}</p>
            </div>
          </div>
        </section>
      )}

      {/* Results Section */}
      {searched && tripRequest && (
        <section className="pb-24 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Trip Request Summary */}
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-6 mb-8" data-testid="trip-summary">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Trip Request</h2>
                <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-mono">
                  {tripRequest.request_id}
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Pick-up</p>
                      <p className="text-white">{tripRequest.pickup_address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Drop-off</p>
                      <p className="text-white">{tripRequest.dropoff_address}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Transport Date</p>
                      <p className="text-white">{tripRequest.transport_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Horses</p>
                      <p className="text-white">{tripRequest.horse_count}</p>
                    </div>
                  </div>
                </div>
              </div>

              {tripRequest.status === 'paid' && (
                <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3" data-testid="trip-paid-banner">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-emerald-400 font-semibold">Payment Complete — Booking Confirmed</p>
                    <p className="text-gray-400 text-sm">This trip is now in your <strong className="text-white">My Bookings</strong>. The transporter has been paid and notified.</p>
                  </div>
                </div>
              )}

              {tripRequest.status === 'confirmed' && (
                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-green-400 font-semibold">Quote Accepted!</p>
                    <p className="text-gray-400 text-sm">Your transport has been confirmed. The transporter will contact you soon.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quotes List */}
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                Available Quotes ({quotes.length})
              </h2>
            </div>

            {quotes.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center" data-testid="no-quotes-message">
                <Clock className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Quotes Yet</h3>
                <p className="text-gray-400">
                  Transporters are reviewing your request. Check back soon for quotes!
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="quotes-list">
                {quotes.map((quote) => (
                  <div
                    key={quote.quote_id}
                    className={`bg-white/5 border rounded-2xl p-6 transition-all duration-300 ${
                      quote.quote_id === acceptedQuoteId 
                        ? 'border-green-500 bg-green-500/5' 
                        : 'border-white/10 hover:border-amber-500/50'
                    }`}
                    data-testid={`quote-card-${quote.quote_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Transporter Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                            <Truck className="w-6 h-6 text-amber-500" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              {quote.transporter_company}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getServiceBadgeColor(quote.service_type)}`}>
                                {quote.service_type?.toUpperCase()}
                              </span>
                              {getStatusBadge(quote.status)}
                            </div>
                          </div>
                        </div>
                        {Array.isArray(quote.transporter_vehicle_photos) && quote.transporter_vehicle_photos.length > 0 && (
                          <div className="flex gap-2 mb-3 flex-wrap" data-testid={`quote-vehicle-photos-${quote.quote_id}`}>
                            {quote.transporter_vehicle_photos.slice(0, 4).map((p, idx) => (
                              <a
                                key={p}
                                href={`${API}/files/${p}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-20 h-16 rounded-lg overflow-hidden border border-white/10 bg-black/30 hover:ring-2 hover:ring-amber-500 transition-all"
                                title="View vehicle photo"
                              >
                                <img src={`${API}/files/${p}`} alt={`Vehicle ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                              </a>
                            ))}
                            {quote.transporter_vehicle_photos.length > 4 && (
                              <div className="w-20 h-16 rounded-lg border border-white/10 bg-black/40 flex items-center justify-center text-gray-300 text-xs font-semibold">
                                +{quote.transporter_vehicle_photos.length - 4}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-gray-400">Estimated Duration</p>
                            <p className="text-white font-medium">{quote.estimated_duration || 'Not specified'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Quote ID</p>
                            <p className="text-amber-500 font-mono text-sm">{quote.quote_id}</p>
                          </div>
                        </div>

                        {quote.notes && (
                          <div className="bg-black/30 rounded-xl p-3 mb-4">
                            <p className="text-gray-400 text-sm mb-1">Notes from transporter:</p>
                            <p className="text-white text-sm">{quote.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Price Section */}
                      <div className="md:w-64 bg-black/30 rounded-xl p-4">
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Transport (Gross)</span>
                            <span className="text-white font-mono">MAD {quote.transport_price?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Service Fee (5%)</span>
                            <span className="text-white font-mono">MAD {quote.customer_fee?.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-white/10 pt-2">
                            <div className="flex justify-between">
                              <span className="text-white font-semibold">Total (Gross)</span>
                              <span className="text-amber-500 font-bold text-xl font-mono">
                                MAD {quote.customer_total?.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Accept Button */}
                        {!acceptedQuoteId && quote.status === 'pending' && (
                          <Button
                            onClick={() => handleAcceptQuote(quote.quote_id)}
                            disabled={acceptingQuoteId === quote.quote_id}
                            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl"
                            data-testid={`accept-quote-btn-${quote.quote_id}`}
                          >
                            {acceptingQuoteId === quote.quote_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Accepting...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Accept Quote
                              </>
                            )}
                          </Button>
                        )}

                        {quote.quote_id === acceptedQuoteId && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-center gap-2 text-green-400 font-semibold py-2">
                              <CheckCircle2 className="w-5 h-5" />
                              Quote Accepted
                            </div>
                            {/* If the trip is already paid OR this quote's status is "paid",
                                show a PAID badge instead of the Pay button. */}
                            {tripRequest?.status === 'accepted' ? (
                              <div
                                className="flex items-center justify-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-xl py-3 px-3 text-blue-300 text-sm font-semibold text-center"
                                data-testid={`awaiting-transporter-${quote.quote_id}`}
                              >
                                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                Waiting for the transporter to confirm — payment opens right after
                              </div>
                            ) : (tripRequest?.status === 'paid' || quote.status === 'paid') ? (
                              <div
                                className="flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl py-3 text-emerald-400 font-bold"
                                data-testid={`paid-badge-${quote.quote_id}`}
                              >
                                <CheckCircle2 className="w-5 h-5" />
                                Paid • MAD {quote.customer_total?.toFixed(2)}
                              </div>
                            ) : (
                              <Button
                                onClick={() =>
                                  navigate(
                                    `/payment/${quote.quote_id}?email=${encodeURIComponent(email)}&amount=${quote.customer_total || ''}`
                                  )
                                }
                                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-xl"
                                data-testid={`pay-now-btn-${quote.quote_id}`}
                              >
                                Pay Now • MAD {quote.customer_total?.toFixed(2)}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* No Results State */}
      {searched && !tripRequest && !loading && !error && (
        <section className="pb-24 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-12">
              <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-4">No Results Found</h3>
              <p className="text-gray-400 mb-6">
                We couldn&apos;t find this trip request. It may have been removed.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full px-8"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CustomerQuotesPage;
